//-- Copyright (c) 2021 TMT Insights Group, LLC
import React from 'react';
import PolarisIcon from '@/components/_common/PolarisIcon';
import styled from '@emotion/styled';
import { Chip, Divider, Fade, Grid, ListItem, Tooltip, IconButton, Typography } from '@mui/material';
import { getUnsupportedPreviewFormats, insertWordBreaks, formatBytes } from '@/_utils/utils';
import ClickCopy from '@/components/_common/ClickCopy/ClickCopy';
import FileType from '@/components/_common/Icons/FileType';
import LabelValue from '@/components/_common/LabelValue';
import { JobDetailInventoryFile } from '@/redux/slices/slice_types';
import { DateTimeDisplay } from '@/exports/columnCommonComponents';
import { useAppSelector } from '@/redux/hooks';
import { selectFileTypeGroupings } from '@/redux/selectors/constantsSelectors';
import { FileTypeGrouping } from '@/constants/constants';

type FilesAsGridProps = {
  files: JobDetailInventoryFile[];
  copyDownloadLink: (file: JobDetailInventoryFile) => void;
  handleDownload: (file: JobDetailInventoryFile) => void;
  onPreviewFile: (file: JobDetailInventoryFile) => void;
  copying: string | null;
  downloading: string | null;
  preparingPreview?: {
    name: string;
    url?: string;
  };
};

const FilesAsGrid = ({ files, copyDownloadLink, handleDownload, onPreviewFile, copying, downloading, preparingPreview }: FilesAsGridProps) => {
  const fileTypeGroupings = useAppSelector(selectFileTypeGroupings) as FileTypeGrouping;
  const disabledFileTypes = getUnsupportedPreviewFormats(fileTypeGroupings.other);

  return (
    <Grid container spacing={5}>
      {!!files.length &&
        files.map((file, index) => {
          const id = `${file.name}_${index}`;
          const ext = file.file_type.startsWith('.') ? file.file_type.substring(1, file.file_type.length) : file.file_type;
          const cantPreview = disabledFileTypes.includes(ext);

          return (
            <Fade key={id} in timeout={(index + 1) * 200}>
              <Grid item xs={12} sm={6} xl={4} alignSelf="stretch">
                <StyledListItem>
                  <FileType type={file.file_type} />
                  <FileNameContainerGrid flexDirection="column" justifyContent="center" alignItems="center">
                    <ClickCopy copyText={file.name} placement="bottom">
                      <FileNameTypography variant="h3">{insertWordBreaks(file.name)}</FileNameTypography>
                    </ClickCopy>
                  </FileNameContainerGrid>
                  <Grid container justifyContent="center" sx={{ mb: 1 }}>
                    <Typography variant="caption">{formatBytes(file.size)}</Typography>
                  </Grid>
                  <Divider flexItem sx={{ mt: 1.5 }} />
                  <FileMetaGrid container justifyContent="space-between">
                    <FileSizeLabelValue label="Source">{file.source_name}</FileSizeLabelValue>
                    <ModifiedLabelValue label="Modified">
                      <DateTimeDisplay singleLine>{file.last_modified}</DateTimeDisplay>
                    </ModifiedLabelValue>
                  </FileMetaGrid>
                  <Divider flexItem sx={{ mb: 1, mt: 'auto' }} />
                  <FileActionsGrid container alignItems="center" flexWrap="nowrap">
                    {!!file.label && <Chip label={insertWordBreaks(file.label)} variant="outlined" color="info" />}
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                      <Tooltip title="Copy Download Link" placement="top">
                        <IconButton
                          sx={{ ml: 'auto', mr: -1, opacity: 0.5 }}
                          size="large"
                          color="default"
                          aria-label="copy"
                          onClick={() => copyDownloadLink(file)}
                        >
                          <PolarisIcon icon={file.label === copying ? 'loading' : 'link'} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download File" placement="top">
                        <IconButton edge="end" size="large" color="primary" aria-label="download" onClick={() => handleDownload(file)}>
                          <PolarisIcon icon={file.name === downloading ? 'loading' : 'download'} />
                        </IconButton>
                      </Tooltip>
                      {cantPreview ? (
                        <Tooltip title="Preview is not supported for this file type">
                          <EyesClosedIconButton color="default">
                            <PolarisIcon icon="hidden" style={{ opacity: 0.3 }} />
                          </EyesClosedIconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Preview" placement="top">
                          <IconButton
                            disabled={cantPreview}
                            edge="end"
                            size="large"
                            color="primary"
                            aria-label="preview file"
                            onClick={() => onPreviewFile(file)}
                          >
                            <PolarisIcon icon={file.name === preparingPreview?.name && !preparingPreview.url ? 'loading' : 'preview'} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </FileActionsGrid>
                </StyledListItem>
              </Grid>
            </Fade>
          );
        })}
    </Grid>
  );
};

export default FilesAsGrid;

const FileActionsGrid = styled(Grid)`
  margin-top: ${({ theme }) => theme.spacing(2)};
  display: flex;
  align-items: center;
  justify-content: space-between;

  & .MuiTypography-subtitle2 {
    opacity: 0.7;
  }

  & .MuiChip-root {
    max-width: calc(100% - 80px);
    padding: ${({ theme }) => theme.spacing(0.75, 0.5)};
    text-align: center;
    height: auto;
  }

  & .MuiChip-label {
    text-transform: uppercase;
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

const FileMetaGrid = styled(Grid)`
  margin-top: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  opacity: 0.7;
`;

const FileNameContainerGrid = styled(Grid)`
  height: 100%;
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding-top: ${({ theme }) => theme.spacing(1)};
  padding-bottom: ${({ theme }) => theme.spacing(1)};

  & span {
    height: 100%;
  }
`;

const FileNameTypography = styled(Typography)`
  font-size: 1.1rem;
  word-break: break-word;
  text-align: center;
  height: 100%;
  padding: ${({ theme }) => theme.spacing(1, 0)};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
`;

const StyledListItem = styled(ListItem)`
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  padding: ${({ theme }) => theme.spacing(5, 5, 3)};
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid ${({ theme }) => theme.palette.action.hover};

  & label {
    margin-bottom: 0;
    opacity: 0.5;
  }
`;

const ModifiedLabelValue = styled(LabelValue)`
  & > label {
    background-color: blue;
  }
`;

const FileSizeLabelValue = styled(LabelValue)`
  margin-bottom: ${({ theme }) => theme.spacing(1)};

  & .MuiTypography-root {
    min-width: auto;
  }
`;

const EyesClosedIconButton = styled(IconButton)`
  color: ${({ theme }) => theme.palette.text.primary};
  opacity: 0.6;
`;
